import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Layout } from "../components/layout/Layout";
import { FaCalendar, FaUser, FaArrowRight } from "react-icons/fa";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const allTags = [...new Set(posts.flatMap((p) => p.tags))];
  const filteredPosts = selectedTag
    ? posts.filter((p) => p.tags.includes(selectedTag))
    : posts;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const [featuredPost, ...restPosts] = filteredPosts;

  return (
    <Layout>
      <Helmet>
        <title>Blog | Crown Amusement - Skill Gaming Industry Insights</title>
        <meta
          name="description"
          content="Stay updated with the latest insights on skill gaming, cabinet manufacturing, industry trends, and gaming technology from Crown Amusement."
        />
        <link rel="canonical" href="https://crownamusement.com/blog" />
        <meta property="og:title" content="Blog | Crown Amusement - Skill Gaming Industry Insights" />
        <meta property="og:description" content="Latest insights on skill gaming, cabinet manufacturing, and gaming technology." />
        <meta property="og:url" content="https://crownamusement.com/blog" />
      </Helmet>

      {/* Hero + Tags */}
      <section className="pt-32 pb-8 bg-obsidian" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-4 tracking-wider uppercase border border-gold-500/20"
          >
            Blog & Insights
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
          >
            Industry <span className="text-gold-gradient">Insights</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-base md:text-lg text-gray-400 max-w-2xl"
          >
            Expert perspectives on skill gaming technology, market trends, and
            the future of the amusement industry.
          </motion.p>

          {/* Tags inline */}
          {allTags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-2 mt-8"
              data-testid="blog-tags-filter"
            >
              <button
                onClick={() => setSelectedTag(null)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  !selectedTag
                    ? "bg-gold-500 text-obsidian"
                    : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"
                }`}
                data-testid="tag-filter-all"
              >
                All Posts
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  data-testid={`tag-filter-${tag}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    selectedTag === tag
                      ? "bg-gold-500 text-obsidian"
                      : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Posts */}
      <section className="py-12 md:py-16 bg-obsidian">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="space-y-8">
              <div className="rounded-2xl bg-surface animate-pulse h-72" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-surface animate-pulse h-64" />
                <div className="rounded-2xl bg-surface animate-pulse h-64" />
              </div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <p className="text-gray-400 text-lg">No blog posts yet.</p>
              <p className="text-gray-500 text-sm mt-2">
                Check back soon for industry insights and news.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Featured Post — full width */}
              {featuredPost && (
                <motion.article
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  data-testid={`blog-card-${featuredPost.slug}`}
                >
                  <Link
                    to={`/blog/${featuredPost.slug}`}
                    className="group block rounded-2xl overflow-hidden bg-surface border border-white/5 hover:border-gold-500/30 transition-all duration-300"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2">
                      {/* Image */}
                      {featuredPost.cover_image ? (
                        <div className="relative h-64 md:h-80 overflow-hidden">
                          <img
                            src={featuredPost.cover_image}
                            alt={featuredPost.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-surface/50 hidden md:block" />
                          <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent md:hidden" />
                        </div>
                      ) : (
                        <div className="h-64 md:h-80 bg-gradient-to-br from-gold-600/20 to-charcoal flex items-center justify-center">
                          <span className="font-heading text-5xl text-gold-500/20">CA</span>
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-6 md:p-8 flex flex-col justify-center">
                        {featuredPost.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {featuredPost.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-3 py-1 text-[10px] font-medium text-gold-400 bg-gold-500/10 rounded-full uppercase tracking-wider"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <h2 className="font-heading text-2xl md:text-3xl text-white group-hover:text-gold-400 transition-colors leading-tight">
                          {featuredPost.title}
                        </h2>
                        {featuredPost.excerpt && (
                          <p className="mt-3 text-sm md:text-base text-gray-400 line-clamp-3 leading-relaxed">
                            {featuredPost.excerpt}
                          </p>
                        )}
                        <div className="mt-5 flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <FaUser className="text-gold-500/60" />
                              {featuredPost.author}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaCalendar className="text-gold-500/60" />
                              {formatDate(featuredPost.created_at)}
                            </span>
                          </div>
                          <span className="flex items-center gap-1 text-gold-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">
                            Read More <FaArrowRight />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              )}

              {/* Rest of posts — responsive grid */}
              {restPosts.length > 0 && (
                <div className={`grid grid-cols-1 ${restPosts.length >= 2 ? "md:grid-cols-2" : ""} gap-6`}>
                  {restPosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      data-testid={`blog-card-${post.slug}`}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className={`group block rounded-2xl overflow-hidden bg-surface border border-white/5 hover:border-gold-500/30 transition-all duration-300 h-full ${
                          restPosts.length === 1 ? "" : ""
                        }`}
                      >
                        {/* Horizontal layout for single remaining post */}
                        {restPosts.length === 1 ? (
                          <div className="grid grid-cols-1 md:grid-cols-5">
                            {post.cover_image ? (
                              <div className="relative h-48 md:h-64 md:col-span-2 overflow-hidden">
                                <img
                                  src={post.cover_image}
                                  alt={post.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-surface/50 hidden md:block" />
                                <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent md:hidden" />
                              </div>
                            ) : (
                              <div className="h-48 md:h-64 md:col-span-2 bg-gradient-to-br from-gold-600/20 to-charcoal flex items-center justify-center">
                                <span className="font-heading text-4xl text-gold-500/20">CA</span>
                              </div>
                            )}
                            <div className="p-6 md:p-8 md:col-span-3 flex flex-col justify-center">
                              {post.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {post.tags.map((tag) => (
                                    <span key={tag} className="px-3 py-1 text-[10px] font-medium text-gold-400 bg-gold-500/10 rounded-full uppercase tracking-wider">{tag}</span>
                                  ))}
                                </div>
                              )}
                              <h2 className="font-heading text-xl md:text-2xl text-white group-hover:text-gold-400 transition-colors">{post.title}</h2>
                              {post.excerpt && <p className="mt-2 text-sm text-gray-400 line-clamp-3 leading-relaxed">{post.excerpt}</p>}
                              <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                                <div className="flex items-center gap-3">
                                  <span className="flex items-center gap-1"><FaUser className="text-gold-500/60" />{post.author}</span>
                                  <span className="flex items-center gap-1"><FaCalendar className="text-gold-500/60" />{formatDate(post.created_at)}</span>
                                </div>
                                <span className="flex items-center gap-1 text-gold-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">Read More <FaArrowRight /></span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Vertical card for multiple remaining posts */
                          <>
                            {post.cover_image ? (
                              <div className="relative h-48 overflow-hidden">
                                <img
                                  src={post.cover_image}
                                  alt={post.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                              </div>
                            ) : (
                              <div className="h-48 bg-gradient-to-br from-gold-600/20 to-charcoal flex items-center justify-center">
                                <span className="font-heading text-3xl text-gold-500/30">CA</span>
                              </div>
                            )}
                            <div className="p-6">
                              {post.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {post.tags.slice(0, 2).map((tag) => (
                                    <span key={tag} className="px-2.5 py-0.5 text-[10px] font-medium text-gold-400 bg-gold-500/10 rounded-full uppercase tracking-wider">{tag}</span>
                                  ))}
                                </div>
                              )}
                              <h2 className="font-heading text-lg text-white group-hover:text-gold-400 transition-colors line-clamp-2">{post.title}</h2>
                              {post.excerpt && <p className="mt-2 text-sm text-gray-400 line-clamp-3 leading-relaxed">{post.excerpt}</p>}
                              <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                                <div className="flex items-center gap-3">
                                  <span className="flex items-center gap-1"><FaUser className="text-gold-500/60" />{post.author}</span>
                                  <span className="flex items-center gap-1"><FaCalendar className="text-gold-500/60" />{formatDate(post.created_at)}</span>
                                </div>
                                <FaArrowRight className="text-gold-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          </>
                        )}
                      </Link>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-obsidian to-charcoal">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white font-semibold">
            Have Questions About Our Products?
          </h2>
          <p className="mt-4 text-gray-400 max-w-xl mx-auto">
            Our team is ready to help you find the perfect gaming solution.
          </p>
          <Link
            to="/contact"
            data-testid="blog-contact-cta"
            className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
          >
            Contact Us <FaArrowRight />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
